import { Component, DestroyRef, OnInit, Input, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TaskDataService } from "../../services/TaskDataService";
import { ControlDataService } from "@app/controls/services/control.data.service";
import { GenericGroupedListConfig } from "@app/shared/models/GenericList/GenericGroupedListConfig";
import { EffectivenessControlOverviewModel } from "@app/controls/testing/advanced/control-overview/shared/effectiveness-control-overview.model";
import { IdNameColorModel } from "@models/generic/IdNameColorModel";
import { TabService } from "@services/tabs/TabService";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { Pages } from "@constants/pages/Pages";
import { of } from "rxjs";
import { nameof } from "@methods/jeffs-toolkit";
import { provideTranslocoScope, TranslocoService } from "@jsverse/transloco";

@Component({
    selector: "task-effectiveness",
    templateUrl: "./task-effectiveness.component.html",
    standalone: false,
    providers: [provideTranslocoScope("tasks")]
})
export class TaskEffectivenessComponent implements OnInit {
    protected _ds = inject(TaskDataService);
    private _controlDataService = inject(ControlDataService);
    private tabService = inject(TabService);
    private pages = inject(Pages);
    private _transloco = inject(TranslocoService);
    private _destroyRef = inject(DestroyRef);

    @Input() id: string;

    effectivenessOverviewConfig: GenericGroupedListConfig;
    scores: IdNameColorModel[];

    async ngOnInit(): Promise<void> {
        this._controlDataService.getAllScores().subscribe((d) => {
            this.scores = d;

            // The list-config strings are read once, outside change detection, so they
            // cannot go through the `transloco` pipe. Load the scope explicitly here and
            // resolve every key against that scope-qualified lang, so the config is only
            // built once the translations are available.
            const scopeLang = `tasks/${this._transloco.getActiveLang()}`;
            this._transloco
                .load(scopeLang)
                .pipe(takeUntilDestroyed(this._destroyRef))
                .subscribe(() => {
                    const t = (key: string) => this._transloco.translate<string>(key, {}, scopeLang);
                    this.createOverviewConfig(t);
                });
        });
    }

    createOverviewConfig(t: (key: string) => string): void {
        this.effectivenessOverviewConfig = <GenericGroupedListConfig>{
            name: t("testPlansConfigName"),
            groupByProp: nameof<EffectivenessControlOverviewModel>((eff) => eff.groupId),
            groupByDisplayName: nameof<EffectivenessControlOverviewModel>((eff) => eff.groupName),
            overviewSortBy: nameof<EffectivenessControlOverviewModel>((eff) => eff.endDate),
            overviewSortAsc: true,

            idProp: nameof<EffectivenessControlOverviewModel>((eff) => eff.guid),

            fields: [
                {
                    fieldName: nameof<EffectivenessControlOverviewModel>((eff) => eff.testPlanName),
                    prettyName: t("testPlanNameColumn"),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    fieldName: nameof<EffectivenessControlOverviewModel>(
                        (eff) => eff.statusDescription
                    ),
                    prettyName: t("columnStatus"),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    fieldName: nameof<EffectivenessControlOverviewModel>((eff) => eff.startDate),
                    prettyName: t("startDateColumn"),
                    fieldType: GenericListFieldType.Date,
                },
                {
                    fieldName: nameof<EffectivenessControlOverviewModel>((eff) => eff.endDate),
                    prettyName: t("endDateColumn"),
                    fieldType: GenericListFieldType.Date,
                },
                {
                    fieldName: nameof<EffectivenessControlOverviewModel>(
                        (eff) => eff.numberOfSamples
                    ),
                    prettyName: t("samplesColumn"),
                    fieldType: GenericListFieldType.Number,
                },
                {
                    fieldName: nameof<EffectivenessControlOverviewModel>((eff) => eff.score),
                    prettyName: t("scoreLabel"),
                    fieldType: GenericListFieldType.ColoredSelect,
                    getDataMethod: () => {
                        return of(this.scores);
                    },
                },
            ],

            cellConfig: {
                testPlanName: {
                    alignLeft: true,
                },
            },

            cellSummaries: {
                testPlanName: {
                    cellStringFormat: t("testPlanGroupSummaryFormat"),
                    embedGroupName: true,
                    embedCount: true,
                },
                startDate: {
                    cellStringFormat:
                        "{{" +
                        nameof<EffectivenessControlOverviewModel>((eff) => eff.groupStartDate) +
                        "}}",
                },
                endDate: {
                    cellStringFormat:
                        "{{" +
                        nameof<EffectivenessControlOverviewModel>((eff) => eff.groupEndDate) +
                        "}}",
                },
                numberOfSamples: {
                    cellStringFormat: "[embedSum]/[embedAvg]",
                    embedSum: true,
                    embedAvg: nameof<EffectivenessControlOverviewModel>(
                        (eff) => eff.groupMinimumSampleCount
                    ), // This will be minimum sample count in future.
                },
                score: {
                    embedColor: { colorCol: "groupScoreColor", textCol: "groupScoreText" },
                },
            },

            overviewRowActionsName: t("details"),
            overviewRowActions: [
                {
                    icon: "fas fa-edit",
                    tooltip: t("openTestPlanDetailsTooltip"),
                    clickEvent: (row: EffectivenessControlOverviewModel) => {
                        this.tabService.generateTab(
                            this.pages.ControlsTestingAdvEffDetail,
                            row.guid
                        );
                    },
                },
            ],
            editOverride: (row: any) => {
                this.tabService.generateTab(this.pages.ControlsTestingAdvEffDetail, row.guid);
                return false;
            },
            dataMethod: () => this._ds.getTaskEffectivenesses(this.id, this.scores),
        };
    }
}
